import { BaseButton as FluentBaseButton } from '@fluentui/react/lib/Button';
import React, { FC, PureComponent } from 'react';

import { Themed, useTheme } from '../../util/theme';
import Block from '../Block';
import Spinner from '../Spinner';

import { getClassNames } from './BaseButton.styles.legacy';
import { BaseButtonProps } from './BaseButton.types';
import { ButtonProps } from './Button.types';

const hrefDenylist = ['', '#', 'javascript://'];

export type ThemedBaseButtonProps = Themed<BaseButtonProps>;

class ButtonComponent extends PureComponent<ThemedBaseButtonProps> {
  public static propTypes = {
    // TypeScript does not support negated types, so we need to do a runtime validation instead.
    href(props: ButtonProps, propName: string, componentName: string) {
      const href = props.href;
      if (typeof href !== 'string') {
        return null;
      }

      if (hrefDenylist.indexOf(href) > -1) {
        return new Error(
          `Invalid prop ${propName} supplied to ${componentName}: "${href}".
          Please use a Button for actions, ExternalLink (or Button with href) for navigation.`
        );
      }

      return null;
    },
    loadingText(props: ButtonProps, propName: string, componentName: string) {
      const { status, loadingText } = props;
      if (status === 'loading' && !loadingText) {
        return new Error(
          `Invalid prop ${propName} supplied to ${componentName}.
          Loading status must be supplemented with loadingText.`
        );
      }

      return null;
    },
    status(props: ButtonProps, propName: string, componentName: string) {
      const { status, href } = props;
      const hasDisabledStatus = status === 'disabled' || status === 'loading';
      if (hasDisabledStatus && href) {
        return new Error(
          `Invalid prop ${propName} supplied to ${componentName}.
          Button rendered as a link (with "href") cannot have a ButtonStatus other than ENABLED.`
        );
      }

      return null;
    },
  };

  public render() {
    const {
      ariaLabel,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      text,
      secondaryText,
      icon: Icon,
      className,
      type = 'button',
      iconPosition = 'left',
      status = 'enabled',
      styles,
      shouldEllipseText = false,
    } = this.props;

    const { onClick, href, loadingText } = this.props;
    const classNames = getClassNames(status, iconPosition, !!text, className);

    const buttonIcon = Icon && (
      <span className={classNames.iconWrapper}>
        <Icon />
      </span>
    );
    const leftIcon = iconPosition === 'left' && buttonIcon;
    const rightIcon = iconPosition === 'right' && buttonIcon;

    const loadingSpinner =
      status === 'loading' && loadingText ? (
        <span className={classNames.spinner}>
          <Spinner size="small" text={loadingText} hideText={true} isCentered={true} />
        </span>
      ) : null;

    const isDisabled = status !== 'enabled';
    const rel = href ? 'nofollow noopener noreferrer' : undefined;
    const target = href ? '_blank' : undefined;
    const buttonType = href ? '' : type;
    const textWrapStyle = shouldEllipseText ? 'ellipsis' : undefined;

    return (
      <FluentBaseButton
        ariaLabel={ariaLabel}
        className={classNames.baseButton}
        disabled={isDisabled}
        href={href}
        rel={rel}
        target={target}
        type={buttonType}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        styles={styles}
      >
        <Block textSize={300} className={classNames.content} textAlign="center" textWrap={textWrapStyle}>
          {leftIcon}
          {text}
          {rightIcon}
          <div className={classNames.secondaryText}>
            {leftIcon}
            {secondaryText}
            {rightIcon}
          </div>
        </Block>
        {loadingSpinner}
      </FluentBaseButton>
    );
  }
}

const BaseButton: FC<BaseButtonProps> = (props) => <ButtonComponent {...props} theme={useTheme()} />;

export default BaseButton;
